/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/DelegationManagement.tsx
 * PURPOSE: Delegation management page — create, list, and revoke approval delegations
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-30
 * COMPLIANCE: SDLC 6.3.1 + Sprint 76 Track 2 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 9: Documentation Standards
 *
 * @business-context
 * - Domain: Approval Delegation, Branch Manager Workflow
 * - Impact: Enables managers to delegate approval authority during absence
 * - Stakeholders: Branch Managers, Delegates (BRANCH_MANAGER role)
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: Tab-based list view, modal form, inline styles (no external CSS lib)
 * - Dependencies: React 18, apiClient, useAuthStore, react-router-dom
 *
 * @cross-references
 * - Design: docs/02-design/18-adrs/06-decisions/DD-0023-delegation-management-ui.md
 * - API: backend/app/api/v1/delegations (GET / POST / PATCH revoke)
 * - Model: backend/app/models/delegation.py (ApprovalDelegation)
 * - Service: backend/app/services/approval_chain_service.py
 * - Tests: backend/tests/01-authentication/session-enforcement/
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth";
import { apiClient } from "../../services/api/client";

// =============================================================================
// Types
// =============================================================================

type DelegationStatus = "active" | "expired" | "revoked";
type ApprovalScope = "leave" | "ot" | "shift_swap" | "all";

interface DelegationUser {
  id: string;
  full_name: string;
  email: string;
}

interface Delegation {
  id: string;
  delegator_id: string;
  delegate_id: string;
  delegate_name: string;
  delegator_name: string;
  approval_types: ApprovalScope[];
  start_date: string;
  end_date: string;
  reason?: string;
  status: DelegationStatus;
  created_at: string;
}

interface CreateDelegationPayload {
  delegate_id: string;
  approval_types: ApprovalScope[];
  start_date: string;
  end_date: string;
  reason?: string;
}

// =============================================================================
// Styles
// =============================================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f3f4f6",
  } as React.CSSProperties,
  nav: {
    background: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: "0 24px",
  } as React.CSSProperties,
  navInner: {
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ max-width
    // cap cố định + margin:auto — kéo sát lề khả dụng ở Desktop (nav cha
    // `.nav` đã có padding riêng, không thêm padding ở đây).
    display: "flex",
    alignItems: "center",
    height: "64px",
    gap: "12px",
  } as React.CSSProperties,
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "#4f46e5",
  } as React.CSSProperties,
  title: {
    fontSize: "18px",
    fontWeight: "bold" as const,
    color: "#1f2937",
    margin: 0,
  } as React.CSSProperties,
  createBtn: {
    marginLeft: "auto",
    background: "#4f46e5",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  } as React.CSSProperties,
  main: {
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ max-width
    // cap cố định + margin:auto — kéo sát lề khả dụng ở Desktop.
    padding: "24px",
  } as React.CSSProperties,
  card: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  } as React.CSSProperties,
  tabBar: {
    display: "flex",
    borderBottom: "1px solid #e5e7eb",
    padding: "0 24px",
  } as React.CSSProperties,
  tab: (active: boolean): React.CSSProperties => ({
    padding: "14px 20px",
    fontSize: "14px",
    fontWeight: active ? 600 : 400,
    color: active ? "#4f46e5" : "#6b7280",
    borderBottom: active ? "2px solid #4f46e5" : "2px solid transparent",
    cursor: "pointer",
    background: "none",
    border: "none",
    borderBottomWidth: "2px",
    borderBottomStyle: "solid",
    borderBottomColor: active ? "#4f46e5" : "transparent",
    transition: "all 0.15s",
  }),
  tableWrap: {
    overflowX: "auto" as const,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
  } as React.CSSProperties,
  th: {
    padding: "12px 16px",
    textAlign: "left" as const,
    fontSize: "12px",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  } as React.CSSProperties,
  td: {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#374151",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle" as const,
  } as React.CSSProperties,
  emptyState: {
    textAlign: "center" as const,
    padding: "48px 24px",
    color: "#9ca3af",
    fontSize: "14px",
  } as React.CSSProperties,
  statusBadge: (status: DelegationStatus): React.CSSProperties => {
    const map: Record<DelegationStatus, React.CSSProperties> = {
      active: {
        background: "#d1fae5",
        color: "#065f46",
        border: "1px solid #a7f3d0",
      },
      expired: {
        background: "#f3f4f6",
        color: "#6b7280",
        border: "1px solid #e5e7eb",
      },
      revoked: {
        background: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fca5a5",
      },
    };
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: 500,
      ...map[status],
    };
  },
  revokeBtn: {
    background: "none",
    border: "1px solid #ef4444",
    color: "#ef4444",
    borderRadius: "6px",
    padding: "4px 12px",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.15s",
  } as React.CSSProperties,
  // Modal overlay
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: "white",
    borderRadius: "16px",
    padding: "28px",
    width: "480px",
    maxWidth: "calc(100vw - 32px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  } as React.CSSProperties,
  modalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1f2937",
    margin: "0 0 20px 0",
  } as React.CSSProperties,
  formGroup: {
    marginBottom: "16px",
  } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px",
  } as React.CSSProperties,
  input: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
    boxSizing: "border-box" as const,
    outline: "none",
  } as React.CSSProperties,
  select: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
    boxSizing: "border-box" as const,
    outline: "none",
    background: "white",
    cursor: "pointer",
  } as React.CSSProperties,
  textarea: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1f2937",
    boxSizing: "border-box" as const,
    outline: "none",
    resize: "vertical" as const,
    minHeight: "80px",
  } as React.CSSProperties,
  dateRow: {
    display: "flex",
    gap: "12px",
  } as React.CSSProperties,
  dateGroup: {
    flex: 1,
  } as React.CSSProperties,
  errorText: {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
  } as React.CSSProperties,
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "24px",
  } as React.CSSProperties,
  cancelBtn: {
    background: "white",
    border: "1px solid #d1d5db",
    color: "#374151",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  } as React.CSSProperties,
  submitBtn: (disabled: boolean): React.CSSProperties => ({
    background: disabled ? "#a5b4fc" : "#4f46e5",
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
  }),
  // Toast
  toast: (type: "success" | "error"): React.CSSProperties => ({
    position: "fixed" as const,
    bottom: "24px",
    right: "24px",
    background: type === "success" ? "#065f46" : "#991b1b",
    color: "white",
    padding: "12px 20px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 500,
    zIndex: 2000,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    animation: "fadeIn 0.2s ease",
  }),
  // Confirm dialog
  confirmBox: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    width: "360px",
    maxWidth: "calc(100vw - 32px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    textAlign: "center" as const,
  } as React.CSSProperties,
  confirmTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: "8px",
  } as React.CSSProperties,
  confirmText: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "20px",
  } as React.CSSProperties,
  confirmActions: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  } as React.CSSProperties,
  confirmRevokeBtn: {
    background: "#ef4444",
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  } as React.CSSProperties,
};

// =============================================================================
// Helpers
// =============================================================================

const SCOPE_LABELS: Record<ApprovalScope, string> = {
  leave: "Duyệt nghỉ phép",
  ot: "Duyệt OT",
  shift_swap: "Duyệt đổi ca",
  all: "Tất cả",
};

const STATUS_LABELS: Record<DelegationStatus, string> = {
  active: "Đang hiệu lực",
  expired: "Đã hết hạn",
  revoked: "Đã thu hồi",
};

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function computeStatus(d: Delegation): DelegationStatus {
  // Backend may return status directly; fall back to computing from dates
  if (d.status === "revoked") return "revoked";
  const now = new Date();
  const end = new Date(d.end_date);
  if (end < now) return "expired";
  return "active";
}

function formatScopeList(types: ApprovalScope[]): string {
  if (!types || types.length === 0) return "—";
  // Backend stores uppercase (e.g. "LEAVE"), SCOPE_LABELS uses lowercase keys
  const normalized = types.map((t) => (typeof t === "string" ? t.toLowerCase() : t) as ApprovalScope);
  if (normalized.includes("all")) return SCOPE_LABELS["all"];
  return normalized.map((t) => SCOPE_LABELS[t] ?? t).join(", ");
}

// =============================================================================
// Main Component
// =============================================================================

const DelegationManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Tab: "created" | "received"
  const [activeTab, setActiveTab] = useState<"created" | "received">("created");

  // Data
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<DelegationUser[]>([]);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<{
    delegate_id: string;
    scope: ApprovalScope;
    start_date: string;
    end_date: string;
    reason: string;
  }>({
    delegate_id: "",
    scope: "leave",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [formError, setFormError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Revoke confirm
  const [revokeTarget, setRevokeTarget] = useState<Delegation | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadDelegations = useCallback(async () => {
    setLoading(true);
    try {
      const role = activeTab === "created" ? "delegator" : "delegate";
      const res = await apiClient.get<Delegation[]>(`/delegations?role=${role}`);
      const data = Array.isArray(res.data) ? res.data : [];
      // Compute effective status client-side in case backend omits it
      const normalized = data.map((d) => ({ ...d, status: computeStatus(d) }));
      setDelegations(normalized);
    } catch {
      setDelegations([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const loadUsers = useCallback(async () => {
    try {
      // Try tenant-users/list (TENANT_ADMIN/OWNER has manage_users permission)
      const res = await apiClient.get<any>(
        "/tenant-users/list?page_size=100"
      );
      const data = res.data;
      const list: DelegationUser[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.users)
            ? data.users
            : [];
      setUsers(list.filter((u) => u.id !== user?.id));
    } catch {
      // BRANCH_MANAGER doesn't have manage_users permission — empty list is OK.
      // User will type delegate email directly (cross-branch delegation support).
      setUsers([]);
    }
  }, [user?.id]);

  useEffect(() => {
    loadDelegations();
  }, [loadDelegations]);

  // Load users when modal opens
  useEffect(() => {
    if (showCreateModal) {
      loadUsers();
    }
  }, [showCreateModal, loadUsers]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // -------------------------------------------------------------------------
  // Create delegation
  // -------------------------------------------------------------------------

  const handleCreateSubmit = async () => {
    setFormError("");

    // Validation
    if (!createForm.delegate_id) {
      setFormError("Vui lòng chọn người được ủy quyền.");
      return;
    }
    if (createForm.delegate_id === user?.id) {
      setFormError("Không thể ủy quyền cho chính mình.");
      return;
    }
    if (!createForm.start_date) {
      setFormError("Vui lòng chọn ngày bắt đầu.");
      return;
    }
    if (!createForm.end_date) {
      setFormError("Vui lòng chọn ngày kết thúc.");
      return;
    }
    if (new Date(createForm.end_date) <= new Date(createForm.start_date)) {
      setFormError("Ngày kết thúc phải sau ngày bắt đầu.");
      return;
    }

    const payload: CreateDelegationPayload = {
      delegate_id: createForm.delegate_id,
      approval_types: createForm.scope === "all" ? ["all"] : [createForm.scope],
      start_date: createForm.start_date,
      end_date: createForm.end_date,
      ...(createForm.reason.trim() ? { reason: createForm.reason.trim() } : {}),
    };

    setSubmitting(true);
    try {
      await apiClient.post("/delegations", payload);
      const delegateName =
        users.find((u) => u.id === createForm.delegate_id)?.full_name ?? "người dùng";
      setShowCreateModal(false);
      resetCreateForm();
      setToast({ message: `Đã ủy quyền cho ${delegateName}`, type: "success" });
      loadDelegations();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Tạo ủy quyền thất bại. Vui lòng thử lại.";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({ delegate_id: "", scope: "leave", start_date: "", end_date: "", reason: "" });
    setFormError("");
  };

  // -------------------------------------------------------------------------
  // Revoke delegation
  // -------------------------------------------------------------------------

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await apiClient.patch(`/delegations/${revokeTarget.id}/revoke`);
      setRevokeTarget(null);
      setToast({ message: "Đã thu hồi ủy quyền", type: "success" });
      loadDelegations();
    } catch {
      setToast({ message: "Thu hồi thất bại. Vui lòng thử lại.", type: "error" });
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderTable = () => {
    if (loading) {
      return (
        <div style={styles.emptyState}>
          <span>Đang tải...</span>
        </div>
      );
    }
    if (delegations.length === 0) {
      return (
        <div style={styles.emptyState}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>📋</div>
          <div>Chưa có ủy quyền nào</div>
        </div>
      );
    }

    return (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                {activeTab === "created" ? "Người được ủy quyền" : "Người ủy quyền"}
              </th>
              <th style={styles.th}>Phạm vi</th>
              <th style={styles.th}>Thời hạn</th>
              <th style={styles.th}>Trạng thái</th>
              <th style={styles.th}>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {delegations.map((d) => (
              <tr key={d.id}>
                <td style={styles.td}>
                  <span style={{ fontWeight: 500 }}>
                    {activeTab === "created" ? d.delegate_name : d.delegator_name}
                  </span>
                </td>
                <td style={styles.td}>{formatScopeList(d.approval_types)}</td>
                <td style={styles.td}>
                  {formatDate(d.start_date)} – {formatDate(d.end_date)}
                </td>
                <td style={styles.td}>
                  <span style={styles.statusBadge(d.status)}>{STATUS_LABELS[d.status]}</span>
                </td>
                <td style={styles.td}>
                  {activeTab === "created" && d.status === "active" ? (
                    <button
                      style={styles.revokeBtn}
                      onClick={() => setRevokeTarget(d)}
                    >
                      Thu hồi
                    </button>
                  ) : (
                    <span style={{ color: "#d1d5db", fontSize: "13px" }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div style={styles.page}>
      {/* Nav bar */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <button style={styles.backBtn} onClick={() => navigate("/dashboard/manager")}>
            ←
          </button>
          <h1 style={styles.title}>Quản lý Ủy quyền</h1>
          <button
            style={styles.createBtn}
            onClick={() => {
              resetCreateForm();
              setShowCreateModal(true);
            }}
          >
            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
            Tạo ủy quyền
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={styles.main}>
        <div style={styles.card}>
          {/* Tabs */}
          <div style={styles.tabBar}>
            <button
              style={styles.tab(activeTab === "created")}
              onClick={() => setActiveTab("created")}
            >
              Đã tạo
            </button>
            <button
              style={styles.tab(activeTab === "received")}
              onClick={() => setActiveTab("received")}
            >
              Đã nhận
            </button>
          </div>

          {/* Table */}
          {renderTable()}
        </div>
      </main>

      {/* ------------------------------------------------------------------ */}
      {/* Create Modal                                                         */}
      {/* ------------------------------------------------------------------ */}
      {showCreateModal && (
        <div style={styles.overlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Tạo ủy quyền mới</h2>

            {/* Người được ủy quyền */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Người được ủy quyền <span style={{ color: "#ef4444" }}>*</span>
              </label>
              {users.length > 0 ? (
                <select
                  style={styles.select}
                  value={createForm.delegate_id}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, delegate_id: e.target.value }))
                  }
                >
                  <option value="">— Chọn người dùng —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={styles.select}
                  type="email"
                  placeholder="Nhập email người được ủy quyền"
                  value={createForm.delegate_id}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, delegate_id: e.target.value }))
                  }
                />
              )}
            </div>

            {/* Phạm vi */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Phạm vi <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                style={styles.select}
                value={createForm.scope}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, scope: e.target.value as ApprovalScope }))
                }
              >
                <option value="leave">Duyệt nghỉ phép</option>
                <option value="ot">Duyệt OT</option>
                <option value="shift_swap">Duyệt đổi ca</option>
                <option value="all">Tất cả</option>
              </select>
            </div>

            {/* Date row */}
            <div style={{ ...styles.formGroup, ...styles.dateRow }}>
              <div style={styles.dateGroup}>
                <label style={styles.label}>
                  Ngày bắt đầu <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={createForm.start_date}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, start_date: e.target.value }))
                  }
                />
              </div>
              <div style={styles.dateGroup}>
                <label style={styles.label}>
                  Ngày kết thúc <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={createForm.end_date}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, end_date: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Lý do */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Lý do</label>
              <textarea
                style={styles.textarea}
                placeholder="Nhập lý do ủy quyền (không bắt buộc)"
                value={createForm.reason}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, reason: e.target.value }))
                }
              />
            </div>

            {/* Error message */}
            {formError && <p style={styles.errorText}>{formError}</p>}

            {/* Actions */}
            <div style={styles.modalActions}>
              <button
                style={styles.cancelBtn}
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
              >
                Hủy
              </button>
              <button
                style={styles.submitBtn(submitting)}
                onClick={handleCreateSubmit}
                disabled={submitting}
              >
                {submitting ? "Đang xử lý..." : "Tạo ủy quyền"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Revoke Confirm Dialog                                                */}
      {/* ------------------------------------------------------------------ */}
      {revokeTarget && (
        <div style={styles.overlay} onClick={() => setRevokeTarget(null)}>
          <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>⚠️</div>
            <p style={styles.confirmTitle}>Xác nhận thu hồi</p>
            <p style={styles.confirmText}>
              Bạn có chắc muốn thu hồi ủy quyền cho{" "}
              <strong>{revokeTarget.delegate_name}</strong>?
            </p>
            <div style={styles.confirmActions}>
              <button style={styles.cancelBtn} onClick={() => setRevokeTarget(null)}>
                Hủy
              </button>
              <button
                style={{ ...styles.confirmRevokeBtn, opacity: revoking ? 0.7 : 1 }}
                onClick={handleRevoke}
                disabled={revoking}
              >
                {revoking ? "Đang thu hồi..." : "Thu hồi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Toast                                                                */}
      {/* ------------------------------------------------------------------ */}
      {toast && <div style={styles.toast(toast.type)}>{toast.message}</div>}
    </div>
  );
};

export default DelegationManagement;
